import { useState } from "react";
import { MessageRetry } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * MessageRetry adopts the round Onyx "offset key" press: both pill layouts
 * (`inline` ghost + `block` destructive) keep their `rounded-full` shape and
 * get a springy 1px press-DOWN on :active with the shared overshoot ease — no
 * notch, no travel block, no ring (those are for rectangular key faces). Press
 * any control below to feel the bounce. The old Framer scale-tap is gone.
 */
export function MessageRetryDemo() {
  return (
    <Section
      id="message-retry"
      title="MessageRetry"
      description="Retry affordance for a failed user message. Two pill layouts: inline (a quiet ghost icon-button for the message hover row) and block (a destructive-tinted helper line with the failure reason). Both keep their round shape and adopt the shared offset-key press — a springy 1px press-DOWN on :active — so the whole lib presses consistently. Press a control to feel the bounce."
    >
      <VariantBlock label="Layouts">
        <MessageRetry layout="inline" onRetry={() => {}} />
        <MessageRetry layout="block" onRetry={() => {}} />
      </VariantBlock>

      <VariantBlock label="Block with a failure reason">
        <MessageRetry
          layout="block"
          reason="Connection lost"
          onRetry={() => {}}
        />
        <MessageRetry
          layout="block"
          reason="Server error (500)"
          onRetry={() => {}}
        />
      </VariantBlock>

      <VariantBlock label="Custom label">
        <MessageRetry layout="inline" label="Try again" onRetry={() => {}} />
        <MessageRetry
          layout="block"
          label="Resend"
          reason="Timed out"
          onRetry={() => {}}
        />
      </VariantBlock>

      <VariantBlock
        label="Retrying"
        caption="isRetrying swaps the icon for a Spinner and force-disables"
      >
        <MessageRetry layout="inline" isRetrying onRetry={() => {}} />
        <MessageRetry
          layout="block"
          isRetrying
          reason="Reconnecting…"
          onRetry={() => {}}
        />
      </VariantBlock>

      <VariantBlock label="States">
        <MessageRetry layout="inline" disabled onRetry={() => {}} />
        <MessageRetry
          layout="block"
          disabled
          reason="Couldn't send"
          onRetry={() => {}}
        />
        <MessageRetry layout="inline" motion={false} onRetry={() => {}} />
      </VariantBlock>

      <VariantBlock label="Interactive">
        <MessageRetryLive />
      </VariantBlock>
    </Section>
  );
}

/** A small wired example: click drives a transient retrying state. */
function MessageRetryLive() {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = () => {
    setRetrying(true);
    setTimeout(() => setRetrying(false), 1400);
  };

  return (
    <MessageRetry
      layout="block"
      reason="Message failed to send"
      isRetrying={retrying}
      onRetry={handleRetry}
    />
  );
}
